import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload } from '../components/icons';
import { Modal } from '../components/Modal';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

interface Signature {
  id: number;
  signed_at: string;
  signatory: { id: number; full_name: string } | null;
}

interface ChequeRecord {
  id: number;
  cheque_number: string;
  payee_name: string;
  amount: string;
  status: string;
  required_signatures: number;
  signatures_collected: number;
  void_reason: string | null;
  issued_at: string | null;
  benefit_claim: { id: number; reference: string; member: { id: number; user: { first_name: string; last_name: string } | null } } | null;
  payment_voucher: { id: number; pv_number: string; vendor_invoice_id: number } | null;
  signatures: Signature[];
}

const STATUS_LABELS: Record<string, string> = {
  pending_signature: 'Pending Signature',
  signed: 'Signed',
  issued: 'Issued',
  voided: 'Voided',
  spoiled: 'Spoiled',
};

const STATUS_BADGE: Record<string, string> = {
  pending_signature: 'badge-scheduled',
  signed: 'badge-active',
  issued: 'badge-active',
  voided: 'badge-urgent',
  spoiled: 'badge-urgent',
};

export function ChequeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError, showSuccess } = useToast();

  const [cheque, setCheque] = useState<ChequeRecord | null>(null);
  const [signing, setSigning] = useState(false);
  const [signForm, setSignForm] = useState({ current_password: '', two_factor_code: '', reference: '' });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const canSign = can('memberships.benefits.cheque.sign');
  const canDisburse = can('memberships.benefits.disburse');
  const fmtMoney = (n: string) => formatMoney(Number(n), settings.currency);

  const load = () => api.get<ChequeRecord>(`/cheques/${id}`).then((res) => setCheque(res.data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openSign = () => {
    setSignForm({ current_password: '', two_factor_code: '', reference: '' });
    setErrors({});
    setSigning(true);
  };

  const submitSign = async (e: FormEvent) => {
    e.preventDefault();
    if (!cheque) return;
    try {
      await api.post(`/cheques/${cheque.id}/sign`, signForm);
      setSigning(false);
      showSuccess('Cheque signed.');
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] });
    }
  };

  const markIssued = async () => {
    if (!cheque) return;
    if (!(await confirmAction(`Mark cheque ${cheque.cheque_number} as issued/handed over?`, { confirmLabel: 'Mark Issued' }))) return;
    try {
      await api.post(`/cheques/${cheque.id}/mark-issued`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const submitVoid = async (e: FormEvent) => {
    e.preventDefault();
    if (!cheque) return;
    try {
      await api.post(`/cheques/${cheque.id}/void`, { void_reason: voidReason });
      setVoiding(false);
      setVoidReason('');
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const downloadPdf = () => cheque && downloadFile(api, `/cheques/${cheque.id}/pdf`, `${cheque.cheque_number}.pdf`);

  if (!cheque) return null;

  const reference = cheque.benefit_claim?.reference ?? cheque.payment_voucher?.pv_number ?? '';
  const backTo = cheque.benefit_claim
    ? `/memberships/benefits/claims/${cheque.benefit_claim.id}`
    : cheque.payment_voucher
      ? `/procurement/payments/${cheque.payment_voucher.vendor_invoice_id}`
      : '/memberships/benefits/cheques';

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label={cheque.benefit_claim ? 'Back to claim' : 'Back'} onClick={() => navigate(backTo)} />
          <h1>{cheque.cheque_number}</h1>
        </div>
        <div className="page-header-actions">
          <span className={`badge ${STATUS_BADGE[cheque.status] ?? 'badge-neutral'}`}>
            {STATUS_LABELS[cheque.status] ?? cheque.status}
          </span>
          <IconButton icon={IconDownload} label="Download PDF" onClick={downloadPdf} />
          {canSign && cheque.status === 'pending_signature' && (
            <button type="button" className="btn btn-primary" onClick={openSign}>
              Sign
            </button>
          )}
          {canDisburse && cheque.status === 'signed' && (
            <button type="button" className="btn btn-primary" onClick={markIssued}>
              Mark Issued
            </button>
          )}
          {canDisburse && !['voided', 'issued'].includes(cheque.status) && (
            <button
              type="button"
              className="btn btn-danger-ghost"
              onClick={() => {
                setVoidReason('');
                setVoiding(true);
              }}
            >
              Void
            </button>
          )}
        </div>
      </header>

      <div className="card">
        <div className="detail-grid">
          <div>
            <h3>{cheque.benefit_claim ? 'Claim' : 'Payment Voucher'}</h3>
            <p>{reference}</p>
          </div>
          {cheque.benefit_claim && (
            <div>
              <h3>Member</h3>
              <p>
                {cheque.benefit_claim.member.user
                  ? `${cheque.benefit_claim.member.user.first_name} ${cheque.benefit_claim.member.user.last_name}`
                  : `Member #${cheque.benefit_claim.member.id}`}
              </p>
            </div>
          )}
          <div>
            <h3>Payee</h3>
            <p>{cheque.payee_name}</p>
          </div>
          <div>
            <h3>Amount</h3>
            <p>{fmtMoney(cheque.amount)}</p>
          </div>
          <div>
            <h3>Signatures required</h3>
            <p>{cheque.signatures_collected} / {cheque.required_signatures}</p>
          </div>
        </div>
        {cheque.void_reason && (
          <p className="alert alert-error">
            <strong>Void reason:</strong> {cheque.void_reason}
          </p>
        )}
      </div>

      <div className="card">
        <h2>Signatures</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Signatory</th>
                <th>Signed At</th>
              </tr>
            </thead>
            <tbody>
              {cheque.signatures.map((sig) => (
                <tr key={sig.id}>
                  <td>{sig.signatory?.full_name ?? '—'}</td>
                  <td className="muted">{new Date(sig.signed_at).toLocaleString()}</td>
                </tr>
              ))}
              {cheque.signatures.length === 0 && (
                <tr>
                  <td colSpan={2} className="muted center">
                    No signatures collected yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {signing && (
        <Modal title={`Sign cheque ${cheque.cheque_number}`} onClose={() => setSigning(false)}>
          <form onSubmit={submitSign} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <p className="muted span-2">
              This re-authenticates you as the individually issued signing credential for this cheque — not a shared
              login. Enter your own account password to confirm.
            </p>
            <label className="span-2">
              Current password *
              <input
                type="password"
                value={signForm.current_password}
                onChange={(e) => setSignForm({ ...signForm, current_password: e.target.value })}
                required
              />
              {errors.current_password && <small className="error">{errors.current_password[0]}</small>}
            </label>
            <label className="span-2">
              Two-factor code (if enabled on your account)
              <input
                value={signForm.two_factor_code}
                onChange={(e) => setSignForm({ ...signForm, two_factor_code: e.target.value })}
              />
              {errors.two_factor_code && <small className="error">{errors.two_factor_code[0]}</small>}
            </label>
            <label className="span-2">
              Type the reference to confirm ({reference}) *
              <input
                value={signForm.reference}
                onChange={(e) => setSignForm({ ...signForm, reference: e.target.value })}
                required
              />
              {errors.reference && <small className="error">{errors.reference[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setSigning(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Sign</button>
            </div>
          </form>
        </Modal>
      )}

      {voiding && (
        <Modal title={`Void cheque ${cheque.cheque_number}`} onClose={() => setVoiding(false)}>
          <form onSubmit={submitVoid} className="form-grid">
            <label className="span-2">
              Reason *
              <textarea rows={3} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} required />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setVoiding(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Void</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
